import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  CreateEventRequest,
  EventRecord,
  UpdateEventRequest,
} from "@/shared/domain/events";
import {
  eventQueryKeys,
  type EventsRequest,
} from "@/shared/domain/events/hooks";
import { type Datasource } from "@/shared/state/visualization";
import { useDatasources } from "@/shared/state/visualization/hooks";
import { type FileSource } from "@/shared/state/visualization/schema/v1";

import { useEphemeralWorkspaceState } from "./hooks";

interface SelectOptions {
  datasetIds?: string[];
  fileIds?: string[];
  topicIds?: string[];
  messagePathIds?: string[];
}

type UseWorkspaceEventsOptions = Partial<{
  select: SelectOptions;
}>;

const unique = (ids: string[]): string[] => [...new Set(ids)];

/**
 * The subset of {@link EventsRequest} a workspace produces. `EventsRequest`
 * leaves every field optional because other callers query by topic or message
 * path; a workspace always states both of its own associations, even when both
 * are empty.
 */
type WorkspaceEventsRequest = EventsRequest & {
  datasetIds: string[];
  fileIds: string[];
};

/**
 * Whether a dataset already in `datasetSourceIds` brings in every event this
 * file would.
 *
 * `Event.get_for_workspace` matches `dataset_ids` transitively (the dataset
 * itself plus every file, topic, and message path under it), so naming a file
 * whose parent dataset is already a datasource adds an id to the request
 * without adding scope. The parent dataset is whatever `associationId` records;
 * a file that records none is never covered.
 */
function datasetSourceCoversFile(
  datasource: FileSource,
  datasetSourceIds: ReadonlySet<string>,
): boolean {
  const parentDatasetId = datasource.value.associationId ?? undefined;
  return parentDatasetId !== undefined && datasetSourceIds.has(parentDatasetId);
}

/**
 * The workspace's event-query identity: the datasets and files that define it,
 * derived from the datasources rather than the (potentially enormous) resolved
 * set of files/topics/message paths. Sending datasource-level ids keeps the
 * request bounded: a dataset with thousands of files is a single association,
 * not thousands.
 *
 * The `/visualize` route expands `?session=` into one ordinary file datasource
 * per member file, each carrying its parent dataset in `associationId`. Those
 * are the only files dropped here, and only when a dataset datasource already
 * covers them. A file the user picked by hand is always named, even when its
 * dataset is also a datasource: it costs the request one id, where a session
 * expands to thousands at once.
 *
 * Dropping a file can never empty the request, because a file is dropped only
 * when its dataset is in `datasetIds`. That matters because an empty request
 * short-circuits to `[]` in `EventService.getWorkspaceEvents`, so an event just
 * created in the workspace would vanish on the next refetch.
 *
 * Reading the parent dataset off the datasource rather than off the resolved
 * file records keeps this a function of `datasources` alone, so neither the
 * request nor the query key built from it changes when data loading finishes.
 *
 * Exported for tests.
 */
export function deriveWorkspaceEventsRequest(
  datasources: Datasource[],
): WorkspaceEventsRequest {
  const datasetIds = unique(
    datasources
      .filter((datasource) => datasource.type === "dataset")
      .map((datasource) => datasource.value.datasetId),
  );

  const datasetSourceIds = new Set(datasetIds);

  return {
    datasetIds,
    fileIds: unique(
      datasources
        .filter((datasource) => datasource.type === "file")
        .filter(
          (datasource) =>
            datasource.derivedFrom === undefined ||
            !datasetSourceCoversFile(datasource, datasetSourceIds),
        )
        .map((datasource) => datasource.value.fileId),
    ),
  };
}

/**
 * Memoized {@link deriveWorkspaceEventsRequest} over the open workspace. Shared
 * between the query and the mutation hooks so their optimistic cache writes land
 * on the same cache slot.
 */
function useWorkspaceEventsRequest(): WorkspaceEventsRequest {
  const datasources = useDatasources();

  return React.useMemo(
    () => deriveWorkspaceEventsRequest(datasources),
    [datasources],
  );
}

export function useWorkspaceEvents(options?: UseWorkspaceEventsOptions) {
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const { isDataLoading } = useEphemeralWorkspaceState();
  const workspaceRequest = useWorkspaceEventsRequest();

  return useQuery({
    queryKey: eventQueryKeys.forWorkspace(orgId, workspaceRequest),
    // One viz-specific request describing the workspace's data sources. The server
    // expands datasets/files transitively, infers each loose file's parent dataset to
    // include its dataset-wide events, and dedupes — so there's nothing to merge here.
    queryFn: ({ signal }) => {
      return events.getWorkspaceEvents(
        {
          datasetIds: workspaceRequest.datasetIds,
          fileIds: workspaceRequest.fileIds,
        },
        // The org scopes which custom-field schema the events come back with.
        { abortSignal: signal, resourceOwnerId: orgId },
      );
    },
    select: (data) => {
      if (options?.select) {
        return selectEvents(data, options.select);
      }

      return data;
    },
    enabled: !isDataLoading,
    staleTime: 300_000, // 5 minutes
  });
}

export function useWorkspaceEvent(eventId: string) {
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  return useQuery({
    queryKey: eventQueryKeys.detail(orgId, eventId),
    queryFn: ({ signal }) => {
      return events.getEventById(eventId, {
        abortSignal: signal,
      });
    },
    staleTime: 300_000, // 5 minutes
  });
}

export function useCreateWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();

  const orgId = useCurrentOrgId();
  const workspaceRequest = useWorkspaceEventsRequest();

  return useMutation({
    mutationFn: function createWorkspaceEvent(request: CreateEventRequest) {
      return events.createEvent(orgId, request);
    },
    onSuccess: (event: EventRecord) => {
      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(event.org_id, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return [...oldData, event];
        },
      );
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(event.org_id),
      });
      // Other workspace views may include this event via its
      // associations — mark them stale for next mount. `refetchType:
      // "none"` avoids re-fetching the currently-active workspace,
      // which we just updated via `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(event.org_id),
        refetchType: "none",
      });
    },
  });
}

export function useDeleteWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const workspaceRequest = useWorkspaceEventsRequest();

  return useMutation<void, Error, EventRecord["event_id"]>({
    mutationFn: function deleteWorkspaceEvent(eventId: string) {
      return events.deleteEvent(eventId);
    },
    onSuccess: (_, eventId) => {
      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(orgId, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return oldData.filter((event) => event.event_id !== eventId);
        },
      );
      // The deleted event may also appear in other list views
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(orgId),
      });
      // Other workspace views may also include this event — mark stale
      // for next mount. `refetchType: "none"` avoids re-fetching the
      // currently-active workspace, which we just updated via
      // `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
        refetchType: "none",
      });
    },
  });
}

export function useUpdateWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const workspaceRequest = useWorkspaceEventsRequest();

  return useMutation<
    EventRecord,
    Error,
    { eventId: string; request: UpdateEventRequest }
  >({
    mutationFn: function updateWorkspaceEvent({ eventId, request }) {
      return events.updateEvent(eventId, request);
    },
    onSuccess: (event: EventRecord) => {
      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(orgId, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }

          return oldData.map((oldEvent) => {
            if (oldEvent.event_id === event.event_id) {
              return event;
            }
            return oldEvent;
          });
        },
      );

      queryClient.setQueryData(
        eventQueryKeys.detail(orgId, event.event_id),
        event,
      );

      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(orgId),
      });
      // Other workspace views may also include this event — mark stale
      // for next mount. `refetchType: "none"` avoids re-fetching the
      // currently-active workspace, which we just updated via
      // `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
        refetchType: "none",
      });
    },
  });
}

function selectEvents(events: EventRecord[], select: SelectOptions) {
  return events.filter((event) => {
    const {
      datasetIds = [],
      fileIds = [],
      topicIds = [],
      messagePathIds = [],
    } = select;

    if (
      datasetIds.length > 0 &&
      event.associations.some((association) =>
        datasetIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      fileIds.length > 0 &&
      event.associations.some((association) =>
        fileIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      topicIds.length > 0 &&
      event.associations.some((association) =>
        topicIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      messagePathIds.length > 0 &&
      event.associations.some((association) =>
        messagePathIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    return false;
  });
}
